import { BRAND } from '@/constants/brand';

export type ThemeMode = 'dark' | 'light';

/** Dark Ink shell used by forecasting dashboards and the main app content area. */
const DARK_SHELL = {
  pageBg: BRAND.ink,
  panelBg: BRAND.electric,
  filterInputBg: '#152a66',
  panelBorder: 'rgba(97, 97, 215, 0.25)',
  filterBorder: 'rgba(97, 97, 215, 0.35)',
  text: '#ffffff',
  /** Figures and labels in tables and charts. */
  dataText: '#ffffff',
  /** Channels of the colour that tints *toward the text*: white on ink, ink on
   *  light. For one-off alphas, as `rgba(${APP_SHELL_THEME.overlayRgb}, 0.55)`,
   *  where no named token fits. */
  overlayRgb: '255, 255, 255',
  textMuted: 'rgba(255, 255, 255, 0.65)',
  /** Body copy, headings and table cells: a touch softer than pure `text`. */
  textStrong: 'rgba(255, 255, 255, 0.92)',
  /** Labels, menu items, secondary buttons. */
  textBody: 'rgba(255, 255, 255, 0.88)',
  /** Character counters, filter "Reset", removable-chip crosses. */
  textSubtle: 'rgba(255, 255, 255, 0.75)',
  /** Metric card titles: quieter than `textMuted` so the value leads. */
  textFaint: 'rgba(255, 255, 255, 0.52)',
  textDisabled: 'rgba(255, 255, 255, 0.28)',
  /** Text on a solid accent fill (selected date, primary button, checked box). */
  onAccent: '#ffffff',
  tableHeader: BRAND.lavanda,
  tableHeaderBg: 'rgba(97, 97, 215, 0.18)',
  /** White rather than the accent. Lavanda on a lavanda tint was the same hue
   *  on itself at 5.06:1; white on the same ground is 15.95:1. */
  tableHeaderText: '#ffffff',
  /** Tables sit straight on the ink page; in light mode they get a white sheet. */
  tableSurfaceBg: 'transparent',
  tableSurfaceShadow: 'none',
  tableRowBorder: 'rgba(255, 255, 255, 0.06)',
  tableRowHoverBg: 'rgba(255, 255, 255, 0.04)',
  tableRowSelectedBg: 'rgba(11, 23, 117, 0.45)',
  /** Opaque sticky column header (replaces rgba header tint on scroll-under columns). */
  tableFixedHeaderBg: '#151a52',
  tableFixedBodyBg: BRAND.ink,
  tableFixedBodyHoverBg: '#0f1648',
  tableFixedBodySelectedBg: '#0f1a52',
  /** Ghost hover on text buttons, segmented control track. */
  hoverBg: 'rgba(255, 255, 255, 0.08)',
  menuHoverBg: 'rgba(255, 255, 255, 0.06)',
  descriptionsBg: 'rgba(11, 23, 117, 0.25)',
  descriptionsStrongBorder: '#ffffff',
  /** Multi-select tags inside the page. */
  selectTagBg: 'rgba(97, 97, 215, 0.35)',
  /** Multi-select tags and the selected segment inside modals. */
  chipBg: 'rgba(97, 97, 215, 0.55)',
  chipBorder: 'rgba(97, 97, 215, 0.65)',
  chipText: '#ffffff',
  chipDisabledBg: 'rgba(97, 97, 215, 0.35)',
  chipDisabledBorder: 'rgba(97, 97, 215, 0.45)',
  popupShadow: 'rgba(0, 0, 0, 0.45)',
  /** KPI / metric summary cards — subtle lift on dark surfaces, not saturated panel blue */
  /** Filter bars and toolbars set into the page. */
  insetPanelBg: 'rgba(11, 23, 117, 0.28)',
  insetPanelBorder: 'rgba(97, 97, 215, 0.22)',
  /** Dark surfaces separate by tone alone; light ones need a hint of lift. */
  cardShadow: 'none',
  /** Clickable tiles pressed into the page (forecasting KPI scorecards). */
  recessBg: 'rgba(0, 0, 0, 0.2)',
  metricCardBg: 'rgba(255, 255, 255, 0.05)',
  metricCardBorder: 'rgba(255, 255, 255, 0.08)',
  /** Nested line-item rows: lavanda at 12% / 18% over ink, flattened to opaque
   *  so fixed columns (which need a solid fill) match the rest of the row. */
  lineItemBg: '#141449',
  lineItemHoverBg: '#1a1a53',
  accent: BRAND.lavanda,
  accentHover: '#7070e0',
  /** Link/anchor text. Lavanda itself is only 3.78:1 on the ink background —
   *  under the 4.5:1 AA floor — so links resolve one step lighter, at 6.0:1. */
  accentText: '#8888e1',
  accentTextHover: '#a5a5ea',
  /** Chart gridlines and reference marks. */
  chartGrid: 'rgba(255, 255, 255, 0.09)',
  chartAxisLine: 'rgba(255, 255, 255, 0.25)',
  chartReference: 'rgba(255, 255, 255, 0.35)',
  positive: '#52c41a',
  negative: '#ff4d4f',
  /** Danger as *text on ink*. antd's dark-algorithm derives a deeper red
   *  (#db2622, 3.89:1) for text buttons; this stays above the AA floor at 7.4:1. */
  negativeText: '#ff7875'
} as const;

export type ShellPalette = { readonly [K in keyof typeof DARK_SHELL]: string };

/**
 * Light shell. Same structure as the dark one: page on a cool off-white, panels
 * and cards on white, ink text. Accent stays lavanda for fills (white on it is
 * 5.0:1) but text-on-page uses a deeper indigo, as lavanda text on the page is 4.6:1
 * and sits too close to the floor at 12px.
 */
const LIGHT_SHELL: ShellPalette = {
  pageBg: '#f5f6fb',
  panelBg: '#ffffff',
  filterInputBg: '#ffffff',
  panelBorder: 'rgba(11, 23, 117, 0.12)',
  filterBorder: 'rgba(11, 23, 117, 0.22)',
  text: BRAND.ink,
  /** Near-black rather than ink: table figures and chart labels are small, and
   *  navy at that size reads as grey on white. */
  dataText: '#111111',
  overlayRgb: '10, 10, 54',
  /* Secondary text is mostly 9–11px (filter labels, card titles, legends), where
     ~5:1 still reads washed out on a light page — so these sit at 7:1 or more. */
  /** 8.8:1 on the page background. */
  textMuted: 'rgba(10, 10, 54, 0.76)',
  textStrong: 'rgba(10, 10, 54, 0.92)',
  textBody: 'rgba(10, 10, 54, 0.88)',
  /** 10.9:1. */
  textSubtle: 'rgba(10, 10, 54, 0.82)',
  /** 7.0:1 — still quieter than textMuted so card values lead. */
  textFaint: 'rgba(10, 10, 54, 0.7)',
  textDisabled: 'rgba(10, 10, 54, 0.4)',
  onAccent: '#ffffff',
  tableHeader: BRAND.electric,
  tableHeaderBg: 'rgba(97, 97, 215, 0.1)',
  tableHeaderText: BRAND.ink,
  tableSurfaceBg: '#ffffff',
  tableSurfaceShadow: '0 0 0 1px rgba(11, 23, 117, 0.08), 0 1px 3px rgba(10, 10, 54, 0.05)',
  tableRowBorder: 'rgba(10, 10, 54, 0.07)',
  tableRowHoverBg: 'rgba(10, 10, 54, 0.03)',
  tableRowSelectedBg: 'rgba(97, 97, 215, 0.12)',
  /** Opaque equivalents of the rgba tints above over the white table sheet, so
   *  fixed columns match the scrolling cells beside them. */
  tableFixedHeaderBg: '#efeffb',
  tableFixedBodyBg: '#ffffff',
  tableFixedBodyHoverBg: '#f7f7f9',
  tableFixedBodySelectedBg: '#efeffb',
  hoverBg: 'rgba(10, 10, 54, 0.06)',
  menuHoverBg: 'rgba(10, 10, 54, 0.05)',
  descriptionsBg: 'rgba(97, 97, 215, 0.06)',
  descriptionsStrongBorder: 'rgba(11, 23, 117, 0.25)',
  selectTagBg: 'rgba(97, 97, 215, 0.14)',
  chipBg: 'rgba(97, 97, 215, 0.16)',
  chipBorder: 'rgba(97, 97, 215, 0.35)',
  chipText: BRAND.ink,
  chipDisabledBg: 'rgba(10, 10, 54, 0.05)',
  chipDisabledBorder: 'rgba(10, 10, 54, 0.12)',
  popupShadow: 'rgba(10, 10, 54, 0.14)',
  cardShadow: '0 1px 2px rgba(10, 10, 54, 0.04), 0 2px 6px rgba(10, 10, 54, 0.04)',
  recessBg: '#ffffff',
  insetPanelBg: '#ffffff',
  insetPanelBorder: 'rgba(11, 23, 117, 0.12)',
  metricCardBg: '#ffffff',
  metricCardBorder: 'rgba(11, 23, 117, 0.1)',
  lineItemBg: '#ececf9',
  lineItemHoverBg: '#e4e4f6',
  accent: BRAND.lavanda,
  accentHover: '#4a4fd4',
  /** 7.4:1 on white, 6.9:1 on the page background. */
  accentText: '#4045c0',
  accentTextHover: BRAND.electric,
  chartGrid: 'rgba(10, 10, 54, 0.08)',
  chartAxisLine: 'rgba(10, 10, 54, 0.25)',
  chartReference: 'rgba(10, 10, 54, 0.4)',
  /** antd green-8 / red-7 (5.6:1 on white): the dark-mode green and red are under 3:1 there. */
  positive: '#237804',
  negative: '#cf1322',
  negativeText: '#cf1322'
};

export const SHELL_PALETTES: Record<ThemeMode, ShellPalette> = {
  dark: DARK_SHELL,
  light: LIGHT_SHELL
};

type ShellToken = keyof ShellPalette;

const cssVarName = (token: ShellToken) =>
  `--shell-${token.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`)}`;

/**
 * Shell tokens as CSS variable references, so styled-components and global CSS
 * pick up the active theme without re-rendering. The values behind them are
 * set per mode on `:root[data-theme]` by `shellThemeCss`.
 *
 * Anything that cannot take a `var()` — antd's token algorithm, or a colour
 * written into an SVG attribute (Recharts) — reads `SHELL_PALETTES[mode]`
 * through `useShellPalette()` instead.
 */
export const APP_SHELL_THEME = Object.fromEntries(
  (Object.keys(DARK_SHELL) as ShellToken[]).map((token) => [token, `var(${cssVarName(token)})`])
) as { readonly [K in ShellToken]: string };

const declarations = (palette: ShellPalette) =>
  (Object.keys(palette) as ShellToken[])
    .map((token) => `${cssVarName(token)}: ${palette[token]};`)
    .join('\n    ');

export const shellThemeCss = `
  :root[data-theme='dark'] {
    color-scheme: dark;
    ${declarations(DARK_SHELL)}
  }

  :root,
  :root[data-theme='light'] {
    color-scheme: light;
    ${declarations(LIGHT_SHELL)}
  }
`;
